// Listing modes for User Management (Directory) — Dashboard / List.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/core/modes.ts / src/action_register/modes.ts, themselves mirrored
// from src/blank/BlankLayout.tsx (Rule 0 — the canonical reference).
//
//   <base>/dashboard  → UserManagementLanding (the honest register snapshot;
//                        was the module's INDEX-mounted content, 802cc0b0)
//   <base>/overview   → redirect to <base>/dashboard — the mode's name until
//                        H94 (Aaron 2026-09-28: "there should be no
//                        'overviews' at a module level - rename these to
//                        dashboard same as .../app/job_costs/dashboard")
//   <base>/list       → UserManagementDataGrid (the working list)
//
// ── THIS SUPERSEDES 802cc0b0's "NO SWITCHER" RULING — FLAGGED, NOT HIDDEN ──
// 802cc0b0 (2026-08-23, Aaron) put the landing at the module index with
// deliberately NO Layout/dropdown: "we don't want switching on the home
// pages." That was an explicit departure from src/companies/CompaniesLayout.tsx
// (same Directory nav section), which already used this exact
// overview/list-as-routes shape. Jay's QA sweep the next day (8a61649f,
// 2026-08-24) touched this module without reversing that call, and the
// following day's four-module switcher rollout (4b89d016) pointedly did not
// include User Management. This change (handoff Task 4) puts User Management
// on the same canonical BreadcrumbDropdownMenu pattern as Companies and the
// four 4b89d016 modules, for consistency across Directory. Confirm with Aaron
// if that reversal was not intended — the prior ruling was explicit and
// recent, not stale.
//
// No new views invented: both routes already existed (overview at the old
// index, list at the old `/list`) — this file only adds a route segment to
// the landing and a switcher between the two.

import type { ModeDescriptions } from "../../components/BreadcrumbDropdownMenu";
import { migrateModeSegment } from "../../components/renamedModeSegments";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in UserManagementList.tsx; adding one here without a matching <Route> gives
 * a dropdown entry that 404s against the module's own route table.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  dashboard: "Dashboard",
  list: "List",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  dashboard: "Users, groups and activity",
  list: "Every user account",
};

/**
 * Dashboard, not List. Unlike src/core's "dashboard" (a real "coming soon"
 * stub), UserManagementLanding is fully built out — real KPIs from /v6/users,
 * /v6/groups, /v6/groups/matrix and /v6/users/history/, honest
 * "unavailable"/empty states, no fabricated figures (see UserManagementLanding
 * .tsx's own header). It was also the sole content at the module index before
 * this change, so defaulting here to Dashboard is not a new destination for a
 * bare visit — the DataGrid remains one click (or one dropdown entry) away.
 */
export const DEFAULT_MODE_SEGMENT = "dashboard";

/**
 * `<module>.viewMode` — matches the shape every other converted module uses
 * (see src/companies "companies.viewMode", src/core "core.viewMode").
 */
export const MODE_STORAGE_KEY = "user_management.viewMode";

/**
 * Is this a real mode segment?
 *
 * hasOwnProperty, NOT `in` — `in` walks the prototype chain, so "constructor",
 * "toString", "valueOf", "__proto__" and "hasOwnProperty" all test true against
 * an object literal. A layout validating with `in` would accept one as a mode
 * and redirect to it, 404ing the module against its own routes PERMANENTLY,
 * since the value is persisted. Regressions f74a205b / 2deb1dee; the prototype
 * chain values are enumerated in src/__tests__/routeModeSegments.test.tsx.
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  } catch {
    // Safari private mode / quota-blocked storage throws on access.
    return DEFAULT_MODE_SEGMENT;
  }
  // A stored "overview" (the mode's name before H94) reads as "dashboard".
  stored = migrateModeSegment(stored);
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment. The base
 * is derived from the LIVE pathname rather than hardcoded, matching every
 * other converted module.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//list", which a browser resolves as a PROTOCOL-RELATIVE URL
  // (host "list") and navigates off-site.
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
